import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { ChevronDown } from "lucide-react";

const BUSINESS_TYPES = [
  "General Business",
  "Retail / E-commerce",
  "Restaurant / Food",
  "Construction",
  "Healthcare",
  "Trucking / Transport",
  "Professional Services",
  "Manufacturing",
];

const TIME_IN_BUSINESS = [
  "Less than 6 months",
  "6-12 months",
  "1-2 years",
  "2-5 years",
  "5+ years",
];

function estimate(businessType, credit, revenue, timeYears) {
  // simple estimator: base 3-8 months revenue, adjusted
  let low = revenue * 3;
  let high = revenue * 8;

  // credit multiplier
  const creditFactor = Math.max(0.4, Math.min(1.3, (credit - 500) / 250 + 0.6));
  low *= creditFactor;
  high *= creditFactor;

  // tenure multiplier
  const tenureFactor = 0.6 + timeYears * 0.15;
  low *= Math.min(1.2, tenureFactor);
  high *= Math.min(1.4, tenureFactor);

  // business type nudge
  const highRisk = ["Restaurant / Food", "Construction"];
  if (highRisk.includes(businessType)) {
    low *= 0.85;
    high *= 0.9;
  }

  return {
    low: Math.max(2500, Math.round(low / 250) * 250),
    high: Math.max(5000, Math.round(high / 500) * 500),
  };
}

function readinessLabel(credit, revenue, timeYears) {
  if (credit >= 680 && revenue >= 40000 && timeYears >= 2) return "Strong funding profile";
  if (credit >= 600 && revenue >= 20000) return "Qualifies for most products";
  if (credit >= 550 && revenue >= 10000) return "Building toward funding readiness";
  return "Let's talk — options still available";
}

const tYears = {
  "Less than 6 months": 0.25,
  "6-12 months": 0.75,
  "1-2 years": 1.5,
  "2-5 years": 3.5,
  "5+ years": 6,
};

export default function FundingCalculator({ onApply }) {
  const [businessType, setBusinessType] = useState(BUSINESS_TYPES[0]);
  const [credit, setCredit] = useState(650);
  const [revenue, setRevenue] = useState(65000);
  const [timeInBiz, setTimeInBiz] = useState(TIME_IN_BUSINESS[2]);

  const { low, high } = useMemo(
    () => estimate(businessType, credit, revenue, tYears[timeInBiz]),
    [businessType, credit, revenue, timeInBiz]
  );

  const readiness = useMemo(
    () => readinessLabel(credit, revenue, tYears[timeInBiz]),
    [credit, revenue, timeInBiz]
  );

  const creditPct = ((credit - 300) / (850 - 300)) * 100;
  const revenuePct = ((revenue - 5000) / (500000 - 5000)) * 100;

  useEffect(() => {}, []);

  return (
    <div
      data-testid="funding-calculator"
      className="relative bg-white rounded-3xl shadow-2xl p-7 md:p-8 border border-white/40 text-slate-900"
      style={{
        boxShadow: "0 30px 80px -20px rgba(88,28,135,0.5), 0 10px 30px -10px rgba(0,0,0,0.2)",
      }}
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-display text-2xl font-extrabold tracking-tight">
            Calculate Your Funding
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Get your instant estimate in seconds
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-100 text-purple-700 text-[10px] font-bold uppercase tracking-widest px-3 py-1">
          <span className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse" />
          LIVE
        </span>
      </div>

      {/* Business Type */}
      <div className="mb-5">
        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
          Business Type
        </label>
        <div className="relative">
          <select
            data-testid="calc-business-type"
            value={businessType}
            onChange={(e) => setBusinessType(e.target.value)}
            className="w-full appearance-none bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl px-4 py-3 pr-10 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition"
          >
            {BUSINESS_TYPES.map((b) => (
              <option key={b}>{b}</option>
            ))}
          </select>
          <ChevronDown className="w-4 h-4 absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {/* Credit Score */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Credit Score
          </label>
          <span data-testid="calc-credit-value" className="font-display text-2xl font-extrabold text-purple-700">
            {credit}
          </span>
        </div>
        <input
          data-testid="calc-credit-slider"
          type="range"
          min={300}
          max={850}
          value={credit}
          onChange={(e) => setCredit(parseInt(e.target.value))}
          className="rc-slider w-full"
          style={{ "--val": `${creditPct}%` }}
        />
        <div className="flex justify-between text-[10px] text-slate-400 mt-1.5 font-medium">
          <span>300</span>
          <span>850</span>
        </div>
      </div>

      {/* Monthly Revenue */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Monthly Revenue
          </label>
          <span data-testid="calc-revenue-value" className="font-display text-2xl font-extrabold text-purple-700">
            ${revenue.toLocaleString()}
          </span>
        </div>
        <input
          data-testid="calc-revenue-slider"
          type="range"
          min={5000}
          max={500000}
          step={1000}
          value={revenue}
          onChange={(e) => setRevenue(parseInt(e.target.value))}
          className="rc-slider w-full"
          style={{ "--val": `${revenuePct}%` }}
        />
        <div className="flex justify-between text-[10px] text-slate-400 mt-1.5 font-medium">
          <span>$5K</span>
          <span>$500K+</span>
        </div>
      </div>

      {/* Time in business */}
      <div className="mb-6">
        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
          Time in Business
        </label>
        <div className="relative">
          <select
            data-testid="calc-time-in-business"
            value={timeInBiz}
            onChange={(e) => setTimeInBiz(e.target.value)}
            className="w-full appearance-none bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl px-4 py-3 pr-10 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition"
          >
            {TIME_IN_BUSINESS.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
          <ChevronDown className="w-4 h-4 absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {/* Result panel */}
      <div className="rounded-2xl bg-gradient-to-br from-purple-50 via-purple-100/70 to-fuchsia-50 border border-purple-200/70 p-5 text-center">
        <p className="text-xs font-semibold uppercase tracking-widest text-purple-600">
          Your Estimated Funding
        </p>
        <motion.div
          key={`${low}-${high}`}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-display text-3xl md:text-4xl font-black mt-2 text-purple-800"
          data-testid="calc-estimate-range"
        >
          $<CountUp end={low} duration={0.6} separator="," preserveValue /> – $
          <CountUp end={high} duration={0.6} separator="," preserveValue />
        </motion.div>
        <p className="text-sm text-slate-600 mt-2 font-medium">{readiness}</p>
        <p className="text-[11px] text-slate-400 mt-2">
          Estimate only. Actual options depend on review.
        </p>
      </div>

      <button
        data-testid="calc-get-options-btn"
        onClick={() =>
          onApply({
            business_type: businessType,
            credit_score: credit,
            monthly_revenue: revenue,
            time_in_business: timeInBiz,
            estimated_range_low: low,
            estimated_range_high: high,
          })
        }
        className="mt-5 w-full rounded-xl bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white font-bold py-3.5 text-sm tracking-wide shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 transition-all hover:-translate-y-0.5"
      >
        Get My Funding Options
      </button>
      <p className="text-center text-[11px] text-slate-400 mt-3">
        No credit pull · Fast approval · Trusted nationwide
      </p>
    </div>
  );
}
